import { Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { ApiError, get, post } from '../api.js';
import { VoiceFollower, relocate, scriptOf, storyOfWord, type Script, type ScriptRead } from '../autocue-follow.js';
import { t } from '../i18n.js';
import { navigate, useLocation } from '../router.js';
import { useSession } from '../session.js';
import { SpeechError, browserSpeechService, listen, microphones, type Listening, type SpeechMode, type SpeechProblem, type SpeechService, type SpeechStatus } from '../speech.js';
import { classes, fmtDateInput, fmtDateTime } from '../util.js';
import { realtime, type WsMessage } from '../ws.js';

/**
 * The autocue: a prompter that follows the playout. When a bulletin is loaded on the playout (Control Room → load),
 * its stories come here in running order with what the presenter reads — the ON texts, and the V.O ones if asked —
 * large, white on black, the story on air marked and brought to the reading line. A bulletin can also be chosen by
 * hand. Mirroring is for the glass of a real prompter. Full screen is the text alone, with its reading line: the bar,
 * the voice's line and the keys' hint stay in the operator's window, and the keys keep working.
 *
 * With the voice on (where the installation allows it, AUTOCUE_SPEECH), the autocue listens to the presenter's
 * microphone and keeps the line being read at the reading line, the words already said dimmed. The keys still
 * work: the next / previous story keys move the voice's place, and a scroll by hand is followed from where it
 * leaves the text.
 *
 * Every autocue screen of a bulletin moves together: moved by someone who may drive the autocue (autocue.send) —
 * by hand, the keys, the ▲ ▼ story buttons (on the text, full screen too) or the voice — a screen says where it is
 * (the story at the reading line and how far into it) and the others glide there; a screen just opened starts where
 * they are (one that only reconnects keeps its place). A screen listening to its own voice follows its voice, not
 * another screen's; a hand's move it follows, and its voice goes on from there. The Control Room sends them all to a
 * story. Εκφόρτωση (who may drive it) empties every screen of the bulletin until the Control Room loads a bulletin on
 * the playout again; the empty page looks for it every two seconds.
 */
interface Read {
  mode: 'on' | 'vo';
  text: string;
}
interface AutocueItem {
  id: string;
  kind: string;
  order: number;
  displayNumber: string | null;
  title: string;
  storyRevision: number | null;
  reads: Read[];
}
interface AutocueData {
  rundown: { id: string; programmeName: string; date: string; revision: number } | null;
  playout: { rundownId: string; player: string; loadedAt: string | null } | null;
  items: AutocueItem[];
  onAir: string | null;
  cued: string | null;
  /** Whether the autocue may listen to the presenter, and how (the installation's AUTOCUE_SPEECH). */
  voice?: SpeechMode;
  /** When the autocue was emptied, while it waits for a bulletin to be loaded again. */
  unloaded?: string | null;
}
interface TodayRow {
  id: string;
  name: string;
}
interface Prefs {
  size: number;
  mirror: boolean;
  withVo: boolean;
  follow: boolean;
  /** The microphone the voice listens to; '' = the system's. */
  mic: string;
}
type Voice = { state: 'off' } | { state: 'starting' } | { state: 'on'; local: boolean; status: SpeechStatus } | { state: 'problem'; problem: SpeechProblem };

const PREFS_KEY = 'airpost.autocue';
const DEFAULT_PREFS: Prefs = { size: 46, mirror: false, withVo: false, follow: true, mic: '' };
/** Safari's names for full screen (an iPad's Safari before 16.4 has only these). */
type WebkitFullscreen = { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => void };
const fullscreenElement = (): Element | null => document.fullscreenElement ?? (document as Document & WebkitFullscreen).webkitFullscreenElement ?? null;
/** How long the ✕ of the window-filling full screen shows after a touch or a move of the mouse. */
const EXIT_SHOWN_MS = 2500;

/** Where the presenter reads, as a share of the screen's height (the ▶ line). */
const READING_LINE = 0.22;
/** The language the presenter reads in. */
const SPEECH_LANG = 'el-GR';
/** After a scroll by hand, the voice waits this long, then follows on from the line the operator left at the reading line. */
const HAND_HOLD_MS = 1500;
/** How quickly the text glides to where the voice is (the time constant of the glide). */
const GLIDE_MS = 200;
/** How quickly a screen glides to where another screen of the bulletin is. */
const FOLLOW_MS = 90;
/** A screen says where it is this often at most while it moves (and once more when it stops). */
const SEND_EVERY_MS = 80;
const NO_READS: ScriptRead[] = [];
type Place = { itemId: string; frac: number };

/** Whose service hears the presenter, as the voice line says it. */
const CLOUD_TEXT: Record<SpeechService, Parameters<typeof t>[0]> = {
  google: 'acVoiceCloudGoogle',
  microsoft: 'acVoiceCloudMicrosoft',
  apple: 'acVoiceCloudApple',
  other: 'acVoiceCloud',
};
const service = browserSpeechService();

const PROBLEM_TEXT: Record<SpeechProblem, Parameters<typeof t>[0]> = {
  unsupported: 'acVoiceUnsupported',
  denied: 'acVoiceDenied',
  'no-on-device': 'acVoiceNoLocal',
  downloading: 'acVoiceDownloading',
  failed: 'acVoiceFailed',
};

function loadPrefs(): Prefs {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<Prefs>) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

export function AutocuePage() {
  const chosen = useLocation().query.get('rundown');
  const { can } = useSession();
  /** Whether this screen moves the bulletin's other screens. */
  const drives = can('autocue.send');
  const [data, setData] = useState<AutocueData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [today, setToday] = useState<TodayRow[]>([]);
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [full, setFull] = useState(false);
  /** Full screen where the browser has none for a page's part (an iPhone): the autocue fills the window instead. */
  const [cover, setCover] = useState(false);
  const [exitShown, setExitShown] = useState(false);
  const exitTimer = useRef(0);
  const [voice, setVoice] = useState<Voice>({ state: 'off' });
  const [mics, setMics] = useState<Array<{ id: string; label: string }>>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const heardRef = useRef<HTMLSpanElement>(null);
  const meterRef = useRef<HTMLSpanElement>(null);
  // the voice: the recogniser, the presenter's place, the words on the page, the last scroll by hand
  const listenRef = useRef<Listening | null>(null);
  const followerRef = useRef<VoiceFollower | null>(null);
  const scriptRef = useRef<Script | null>(null);
  const atRef = useRef(0);
  const wordsRef = useRef<HTMLElement[]>([]);
  const paintedRef = useRef(0);
  const handRef = useRef(0);
  const reanchorRef = useRef(false);
  // the screens together: until when this screen's moves are its own (and told), where another screen asked it to go
  const ownRef = useRef(0);
  /** What moves this screen now and is told to the others: a hand, or the voice's glide. */
  const byRef = useRef<'hand' | 'voice'>('hand');
  const sentAtRef = useRef(0);
  const sendTimer = useRef(0);
  const remoteRef = useRef<Place | null>(null);
  const followRaf = useRef(0);
  const placedRef = useRef(false);
  const goRef = useRef<(id: string) => void>(() => undefined);
  const emptiedRef = useRef<() => void>(() => undefined);
  // the place at the reading line when the screen last stopped: kept when the screen or the letters change size
  const keptRef = useRef<Place | null>(null);
  const keptAtRef = useRef(0);
  const keepTimer = useRef(0);
  const [meterNote, setMeterNote] = useState<string | null>(null);
  const set = (p: Partial<Prefs>) => setPrefs((x) => ({ ...x, ...p }));

  const load = useCallback(async () => {
    try {
      const next = await get<AutocueData>(`/api/autocue${chosen ? `?rundownId=${encodeURIComponent(chosen)}` : ''}`);
      // the same stories keep the same list, so the page (and the voice's place in it) is not rebuilt every 5 seconds
      setData((d) => (d && JSON.stringify(d.items) === JSON.stringify(next.items) ? { ...next, items: d.items } : next));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [chosen]);
  const waiting = !!data && !data.rundown;
  // a bulletin loaded on the playout shows up within seconds (an empty autocue looks every two); the socket below brings the on-air row at once
  useEffect(() => {
    const first = window.setTimeout(() => void load(), 0);
    const every = window.setInterval(() => void load(), waiting ? 2000 : 5000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(every);
    };
  }, [load, waiting]);
  useEffect(() => {
    get<{ rundowns: TodayRow[] }>('/api/rundowns/today')
      .then((r) => setToday(r.rundowns))
      .catch(() => setToday([]));
  }, []);

  const rid = data?.rundown?.id ?? null;

  /** Where this screen is: the story (or separator) at the reading line, and how far into it. */
  const placeOf = useCallback((): Place | null => {
    const screen = screenRef.current;
    if (!screen) return null;
    const line = screen.scrollTop + screen.clientHeight * READING_LINE;
    const all = screen.querySelectorAll<HTMLElement>('[data-ac]');
    let el: HTMLElement | null = all[0] ?? null;
    for (const e of all) {
      if (topIn(screen, e) <= line) el = e;
      else break;
    }
    if (!el) return null;
    return { itemId: el.dataset.ac!, frac: Math.max(-50, Math.min(50, (line - topIn(screen, el)) / Math.max(1, el.offsetHeight))) };
  }, []);
  /** The scroll that brings a place to the reading line, or null for a story this screen does not have. */
  const topFor = useCallback((p: Place): number | null => {
    const screen = screenRef.current;
    const el = document.getElementById(`ac-${p.itemId}`);
    if (!screen || !el) return null;
    return Math.max(0, Math.min(screen.scrollHeight - screen.clientHeight, topIn(screen, el) + p.frac * el.offsetHeight - screen.clientHeight * READING_LINE));
  }, []);
  /** This screen is being moved here, for so long: its moves are told to the others, and theirs wait. */
  const own = (ms: number) => {
    ownRef.current = Math.max(ownRef.current, performance.now() + ms);
    byRef.current = 'hand';
    placedRef.current = true;
    remoteRef.current = null;
  };
  const tell = useCallback(() => {
    const p = placeOf();
    sentAtRef.current = performance.now();
    if (p && rid) realtime.autocueAt(rid, p.itemId, p.frac, byRef.current);
  }, [placeOf, rid]);
  /** The screen scrolled: if this screen was moved here (not by another), the others are told, a few times a second. */
  const onScroll = () => {
    // the place at the line, kept as the screen moves (a few times a second) and once it stops
    if (performance.now() - keptAtRef.current > 250) {
      keptAtRef.current = performance.now();
      keptRef.current = placeOf();
    }
    window.clearTimeout(keepTimer.current);
    keepTimer.current = window.setTimeout(() => (keptRef.current = placeOf()), 150);
    if (!drives || !rid || performance.now() > ownRef.current) return;
    if (performance.now() - sentAtRef.current >= SEND_EVERY_MS) tell();
    window.clearTimeout(sendTimer.current);
    sendTimer.current = window.setTimeout(tell, SEND_EVERY_MS + 40);
  };
  useEffect(
    () => () => {
      window.clearTimeout(sendTimer.current);
      window.clearTimeout(keepTimer.current);
    },
    [],
  );
  /**
   * The same words at the reading line after the screen changed size (full screen, the window) or the letters did.
   * Not while the voice leads the screen, or another screen does: they bring it to their place themselves.
   */
  const keepPlace = useCallback(() => {
    const screen = screenRef.current;
    if (!screen || followerRef.current || remoteRef.current) return;
    const to = keptRef.current ? topFor(keptRef.current) : null;
    if (to !== null && Math.abs(to - screen.scrollTop) >= 1) screen.scrollTop = to;
  }, [topFor]);
  useEffect(() => {
    const screen = screenRef.current;
    if (!screen || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => keepPlace());
    ro.observe(screen);
    return () => ro.disconnect();
  }, [rid, keepPlace]);
  useLayoutEffect(() => keepPlace(), [prefs.size, prefs.withVo, keepPlace]);
  /** Glide to where another screen is (straight there the first time, or from far away). */
  const follow = useCallback(
    (p: Place) => {
      const screen = screenRef.current;
      if (!screen) return;
      const target = topFor(p);
      if (target === null) return;
      if (!placedRef.current || Math.abs(target - screen.scrollTop) > screen.clientHeight * 2) {
        placedRef.current = true;
        screen.scrollTop = target;
        return;
      }
      remoteRef.current = p;
      if (followRaf.current) return;
      let last = performance.now();
      const tick = (now: number) => {
        const s = screenRef.current;
        const to = remoteRef.current ? topFor(remoteRef.current) : null;
        if (!s || to === null) {
          followRaf.current = 0;
          return;
        }
        const d = to - s.scrollTop;
        const dt = Math.min(100, now - last);
        last = now;
        if (Math.abs(d) < 2) {
          s.scrollTop = to;
          remoteRef.current = null;
          followRaf.current = 0;
          return;
        }
        // at least a pixel a frame: a scroll position is whole pixels, a smaller step would never get there
        s.scrollTop += Math.sign(d) * Math.max(1, Math.abs(d) * (1 - Math.exp(-dt / FOLLOW_MS)));
        followRaf.current = requestAnimationFrame(tick);
      };
      followRaf.current = requestAnimationFrame(tick);
    },
    [topFor],
  );
  useEffect(() => () => cancelAnimationFrame(followRaf.current), []);

  useEffect(() => {
    if (!rid) return;
    const unsub = realtime.subscribe(rid);
    placedRef.current = false;
    const leave = realtime.joinAutocue(rid, 'screen');
    const off = realtime.listen((m: WsMessage) => {
      if (m.rundownId !== rid) return;
      if (m.type === 'autocue-at' && typeof m.itemId === 'string' && typeof m.frac === 'number') {
        // where the others are, for a screen just opened: a screen already in use (a socket that reconnects) keeps its place
        if (m.join && placedRef.current) return;
        // being moved here by hand: this screen leads until it stops
        if (performance.now() < ownRef.current) return;
        // listening to its own voice: another screen's voice is not followed, a hand is (and the voice goes on from there)
        if (followerRef.current && m.by === 'voice') return;
        handRef.current = performance.now();
        reanchorRef.current = !!followerRef.current;
        follow({ itemId: m.itemId, frac: m.frac });
        return;
      }
      if (m.type === 'autocue-go' && typeof m.itemId === 'string') {
        placedRef.current = true;
        goRef.current(m.itemId);
        return;
      }
      if (m.type === 'autocue-unload') {
        emptiedRef.current();
        return;
      }
      if (m.type === 'sync') {
        const items = (m.items ?? []) as Array<{ itemId: string; target: string; downstreamStatus: string | null }>;
        const at = (s: string) => items.find((x) => x.target === 'playout' && x.downstreamStatus === s)?.itemId ?? null;
        setData((d) => (d ? { ...d, onAir: at('playing'), cued: at('cued') } : d));
      }
      if (m.type === 'rundown') void load();
    });
    return () => {
      unsub();
      leave();
      off();
    };
  }, [rid, load, follow]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch {
      // a private window: the choices last for the page
    }
  }, [prefs]);
  useEffect(() => {
    const h = () => setFull(!!rootRef.current && fullscreenElement() === rootRef.current);
    document.addEventListener('fullscreenchange', h);
    document.addEventListener('webkitfullscreenchange', h);
    return () => {
      document.removeEventListener('fullscreenchange', h);
      document.removeEventListener('webkitfullscreenchange', h);
    };
  }, []);
  // in full screen the keys go to the text (the button that asked for it is no longer on the screen)
  useEffect(() => {
    if (full || cover) screenRef.current?.focus({ preventScroll: true });
  }, [full, cover]);
  useEffect(() => () => window.clearTimeout(exitTimer.current), []);

  const items = data?.items;
  const script = useMemo(() => scriptOf(items ?? [], prefs.withVo), [items, prefs.withVo]);

  /** Dim the words said, up to the presenter's place. */
  const paint = useCallback((at: number) => {
    const words = wordsRef.current;
    const to = Math.min(at, words.length);
    for (let i = to; i < paintedRef.current; i++) words[i]?.classList.remove('said');
    for (let i = paintedRef.current; i < to; i++) words[i]?.classList.add('said');
    paintedRef.current = to;
  }, []);
  const place = useCallback(
    (at: number) => {
      followerRef.current?.moveTo(at);
      atRef.current = followerRef.current?.at ?? at;
      paint(atRef.current);
    },
    [paint],
  );
  /** The first word at the reading line or below it. */
  const wordAtLine = useCallback(() => {
    const screen = screenRef.current;
    const words = wordsRef.current;
    if (!screen || !words.length) return 0;
    const line = screen.scrollTop + screen.clientHeight * READING_LINE - 4;
    let lo = 0;
    let hi = words.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (topIn(screen, words[mid]!) < line) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }, []);

  // the words as drawn, after every change of the text; the voice keeps its place in a story edited under it
  useLayoutEffect(() => {
    wordsRef.current = Array.from(screenRef.current?.querySelectorAll<HTMLElement>('.ac-w') ?? []);
    paintedRef.current = 0;
    const f = followerRef.current;
    if (f && scriptRef.current) {
      f.reset(script.keys, relocate(scriptRef.current, script, f.at));
      atRef.current = f.at;
      paint(f.at);
    }
    scriptRef.current = script;
  }, [script, rid, paint]);

  /** Bring a story to the reading line. */
  const bringUp = useCallback((id: string) => {
    const screen = screenRef.current;
    const el = document.getElementById(`ac-${id}`);
    if (!screen || !el) return;
    screen.scrollTo({ top: Math.max(0, topIn(screen, el) - screen.clientHeight * READING_LINE), behavior: 'smooth' });
  }, []);
  const onAir = data?.onAir ?? null;
  useEffect(() => {
    if (!prefs.follow || !onAir) return;
    const f = followerRef.current;
    if (!f) return bringUp(onAir);
    // with the voice on, the playout only catches the place up (a story read but not heard), never takes it back
    const story = scriptRef.current?.stories.find((s) => s.id === onAir);
    if (story && story.start > f.at) place(story.start);
  }, [onAir, prefs.follow, bringUp, place]);

  const voiceMode: SpeechMode = data?.voice ?? 'off';
  const following = voice.state === 'starting' || voice.state === 'on';
  const endVoice = useCallback(
    (next: Voice) => {
      listenRef.current?.stop();
      listenRef.current = null;
      followerRef.current = null;
      atRef.current = 0;
      paint(0);
      if (heardRef.current) heardRef.current.textContent = '';
      setVoice(next);
    },
    [paint],
  );
  const startVoice = async (deviceId: string, at?: number) => {
    if (followerRef.current || voiceMode === 'off') return;
    const f = new VoiceFollower(script.keys, at ?? wordAtLine());
    followerRef.current = f;
    scriptRef.current = script;
    atRef.current = f.at;
    paint(f.at);
    handRef.current = 0;
    reanchorRef.current = false;
    setVoice({ state: 'starting' });
    try {
      const l = await listen({
        mode: voiceMode,
        lang: SPEECH_LANG,
        deviceId,
        onText: (text, final) => {
          if (followerRef.current !== f) return;
          const now = f.hear(text, final);
          if (now !== atRef.current) {
            atRef.current = now;
            paint(now);
          }
          if (heardRef.current) heardRef.current.textContent = text.split(/\s+/).slice(-9).join(' ');
        },
        onStatus: (status) => {
          if (followerRef.current === f) setVoice((v) => (v.state === 'on' ? { ...v, status } : v));
        },
        onProblem: (problem) => {
          if (followerRef.current === f) endVoice({ state: 'problem', problem });
        },
      });
      if (followerRef.current !== f) return l.stop();
      listenRef.current = l;
      setVoice({ state: 'on', local: l.local, status: 'listening' });
      setMics(await microphones()); // their names, now that the page may use one
    } catch (e) {
      if (followerRef.current === f) endVoice({ state: 'problem', problem: e instanceof SpeechError ? e.problem : 'failed' });
    }
  };
  const toggleVoice = () => {
    if (following) endVoice({ state: 'off' });
    else void startVoice(prefs.mic);
  };
  /** The autocue was emptied (here or on another screen): the text goes, the voice stops, the page waits for a load. */
  const emptied = () => {
    if (followerRef.current) endVoice({ state: 'off' });
    setData((d) => (d ? { ...d, rundown: null, items: [], onAir: null, cued: null, unloaded: d.unloaded ?? new Date().toISOString() } : d));
    if (chosen) navigate('/autocue');
    else void load();
  };
  useEffect(() => {
    emptiedRef.current = emptied;
  });
  const unload = async () => {
    if (!window.confirm(t('acUnloadConfirm'))) return;
    try {
      await post('/api/autocue/unload', rid ? { rundownId: rid } : {});
      emptied();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  const chooseMic = (id: string) => {
    set({ mic: id });
    if (!following) return;
    const at = atRef.current;
    endVoice({ state: 'off' });
    void startVoice(id, at);
  };
  useEffect(() => {
    if (voiceMode === 'off') return;
    let live = true;
    void microphones().then((m) => live && setMics(m));
    return () => {
      live = false;
    };
  }, [voiceMode]);
  useEffect(() => () => listenRef.current?.stop(), []);

  // with the voice on: the line being read glides to the reading line, and the microphone's level shows
  useEffect(() => {
    if (!following) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(100, now - last);
      last = now;
      const screen = screenRef.current;
      if (screen && now - handRef.current > HAND_HOLD_MS) {
        if (reanchorRef.current) {
          reanchorRef.current = false;
          place(wordAtLine());
        }
        const words = wordsRef.current;
        const el = words[Math.min(atRef.current, words.length - 1)];
        if (el) {
          const d = Math.max(0, topIn(screen, el) - screen.clientHeight * READING_LINE) - screen.scrollTop;
          if (Math.abs(d) >= 1) {
            if (ownRef.current < now) byRef.current = 'voice';
            ownRef.current = Math.max(ownRef.current, now + 150);
            placedRef.current = true;
            screen.scrollTop += Math.sign(d) * Math.min(Math.abs(d), Math.max(1, Math.abs(d) * (1 - Math.exp(-dt / GLIDE_MS))));
          }
        }
      }
      const m = meterRef.current;
      if (m) {
        // an unknown level shows as unknown, not as a silent microphone; the words heard, where the browser gives no level
        const l = listenRef.current;
        const level = l ? l.level() : null;
        m.style.transform = `scaleX(${level?.value ?? 0})`;
        const box = m.parentElement;
        const kind = level === null ? 'na' : level.source;
        if (box && box.dataset.kind !== kind) {
          box.dataset.kind = kind;
          box.classList.toggle('na', kind === 'na');
          box.classList.toggle('words', kind === 'words');
          box.title = kind === 'na' ? t('acMeterNa') : kind === 'words' ? t('acMeterWords') : '';
          setMeterNote(kind === 'mic' || !l ? null : `${kind === 'words' ? t('acMeterWords') : t('acMeterNa')} (${l.diag()})`);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [following, place, wordAtLine]);
  /** A scroll by hand while the voice follows: hold, then follow on from where the operator left the text. */
  const byHand = () => {
    if (!following) return;
    handRef.current = performance.now();
    reanchorRef.current = true;
  };
  /** Moved by hand on this screen: by the wheel, a finger (and the glide after it), the scroll bar. */
  const handMove = (ms: number) => {
    own(ms);
    byHand();
  };
  /** Pressed on the text (a finger, the scroll bar): this screen's own until the press ends, wherever it ends, and its glide after. */
  const pressed = (e: ReactPointerEvent) => {
    handMove(60_000);
    const touch = e.pointerType === 'touch';
    const up = () => {
      ownRef.current = performance.now() + (touch ? 1800 : 700);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const stories = (data?.items ?? []).filter((i) => i.kind !== 'separator');
  /** The story at the reading line, for the next / previous keys. */
  const storyAtLine = () => {
    const screen = screenRef.current;
    if (!screen) return -1;
    const line = screen.scrollTop + screen.clientHeight * READING_LINE + 2;
    let k = -1;
    stories.forEach((s, i) => {
      const el = document.getElementById(`ac-${s.id}`);
      if (el && topIn(screen, el) <= line) k = i;
    });
    return k;
  };
  /** The next / previous story for the voice: the next one with words, or the start of this one (then the one before). */
  const voiceStory = (dir: 1 | -1) => {
    const at = atRef.current;
    const k = storyOfWord(script, at);
    const all = script.stories;
    if (dir === 1) {
      const next = all.slice(k + 1).find((s) => s.start > at);
      if (next) place(next.start);
      return;
    }
    const here = all[k];
    if (here && at > here.start + 2) return place(here.start);
    const before = [...all].reverse().find((s) => s.start < (here?.start ?? 0));
    place(before ? before.start : 0);
  };
  /** The next / previous story: the voice's place with the voice on, else the story brought to the reading line. */
  const storyStep = (dir: 1 | -1) => {
    own(900);
    if (following) return voiceStory(dir);
    const k = storyAtLine();
    const to = dir === 1 ? stories[k + 1] : stories[Math.max(0, k - 1)];
    if (to) bringUp(to.id);
  };
  /** The Control Room sent the autocue to a story (kept up to date with the text, for the socket's listener). */
  useEffect(() => {
    goRef.current = (id: string) => {
      const s = script.stories.find((x) => x.id === id);
      if (followerRef.current && s) {
        handRef.current = 0;
        place(s.start);
      } else bringUp(id);
    };
  });
  const toggleFull = () => {
    if (cover) return setCover(false);
    const d = document as Document & WebkitFullscreen;
    if (fullscreenElement()) {
      if (d.exitFullscreen) void d.exitFullscreen().catch(() => undefined);
      else d.webkitExitFullscreen?.();
      return;
    }
    const el = rootRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    if (!el) return;
    if (el.requestFullscreen) void el.requestFullscreen().catch(() => setCover(true));
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else setCover(true);
  };
  /** The window-filling full screen's ✕, for a moment after a touch or a move (an iPhone has no Esc). */
  const showExit = () => {
    if (!cover) return;
    setExitShown(true);
    window.clearTimeout(exitTimer.current);
    exitTimer.current = window.setTimeout(() => setExitShown(false), EXIT_SHOWN_MS);
  };
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const screen = screenRef.current;
    if (!screen) return;
    const step = prefs.size * 1.4;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      handMove(700);
      screen.scrollBy({ top: e.key === 'ArrowDown' ? step : -step, behavior: 'smooth' });
    } else if (e.key === ' ' || e.key === 'PageDown') storyStep(1);
    else if (e.key === 'PageUp') storyStep(-1);
    else if (e.key === 'Home') {
      own(900);
      if (following) place(0);
      else screen.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (e.key === 'f' || e.key === 'F') toggleFull();
    else if (e.key === 'Escape' && cover) setCover(false);
    else if (e.key === 'm' || e.key === 'M') set({ mirror: !prefs.mirror });
    else if ((e.key === 'v' || e.key === 'V') && voiceMode !== 'off') toggleVoice();
    else return;
    e.preventDefault();
  };

  const rundown = data?.rundown ?? null;
  const fromPlayout = !chosen && !!data?.playout && data.playout.rundownId === rundown?.id;
  return (
    <div ref={rootRef} className={classes('ac', (full || cover) && 'full', cover && 'cover', following && 'voice')} onPointerMove={showExit} onPointerDown={showExit}>
      <div className="ac-bar">
        <div className="ac-source">
          <span className={classes('ac-dot', fromPlayout && 'live')} aria-hidden="true" />
          <b>AUTOCUE</b>
          {rundown && (
            <>
              <span className="ac-prog">{rundown.programmeName}</span>
              <span>{fmtDateInput(rundown.date)}</span>
              <span className="ac-muted">{chosen ? t('acChosen') : fromPlayout && data?.playout?.loadedAt ? `${t('acLoadedAt')} ${fmtDateTime(data.playout.loadedAt)}` : t('acFromPlayout')}</span>
            </>
          )}
        </div>
        <div className="ac-tools">
          {voiceMode !== 'off' && rundown && (
            <span className="ac-voice">
              <button type="button" className={classes('ac-btn', following && 'on')} onClick={toggleVoice} title={t('acVoiceTitle')} aria-pressed={following}>
                🎙 {t('acVoice')}
              </button>
              {following && (
                <span className="ac-meter" aria-hidden="true">
                  <span ref={meterRef} />
                </span>
              )}
              <select value={prefs.mic} onChange={(e) => chooseMic(e.target.value)} aria-label={t('acMic')} title={t('acMic')}>
                <option value="">{t('acMicDefault')}</option>
                {mics.map((m, i) => (
                  <option key={m.id} value={m.id}>
                    {m.label || `${t('acMic')} ${i + 1}`}
                  </option>
                ))}
                {prefs.mic && !mics.some((m) => m.id === prefs.mic) && <option value={prefs.mic}>{t('acMic')}</option>}
              </select>
            </span>
          )}
          {drives && rundown && (
            <button type="button" className="ac-btn unload" onClick={() => void unload()} title={t('acUnloadHint')}>
              ⏏ {t('acUnload')}
            </button>
          )}
          <select value={chosen ?? ''} onChange={(e) => navigate(e.target.value ? `/autocue?rundown=${e.target.value}` : '/autocue')} aria-label={t('bulletin')}>
            <option value="">▶ {t('acFromPlayout')}</option>
            {today.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
            {chosen && rundown && !today.some((r) => r.id === chosen) && <option value={chosen}>{`${rundown.programmeName} · ${fmtDateInput(rundown.date)}`}</option>}
          </select>
          <label className="ac-check">
            <input type="checkbox" checked={prefs.follow} onChange={(e) => set({ follow: e.target.checked })} /> {t('acFollow')}
          </label>
          <label className="ac-check">
            <input type="checkbox" checked={prefs.withVo} onChange={(e) => set({ withVo: e.target.checked })} /> {t('acWithVo')}
          </label>
          <button type="button" className="ac-btn" onClick={() => set({ size: Math.max(24, prefs.size - 4) })} title={t('acSmaller')}>
            A−
          </button>
          <button type="button" className="ac-btn" onClick={() => set({ size: Math.min(96, prefs.size + 4) })} title={t('acLarger')}>
            A+
          </button>
          <button type="button" className={classes('ac-btn', prefs.mirror && 'on')} onClick={() => set({ mirror: !prefs.mirror })} title={t('acMirror')}>
            ⇋
          </button>
          <button type="button" className="ac-btn" onClick={toggleFull} title={t('acFullscreen')}>
            ⛶
          </button>
        </div>
      </div>
      {voice.state !== 'off' && (
        <div className={classes('ac-voiceline', voice.state === 'problem' && 'bad')} role="status">
          {voice.state === 'starting' && <span>{t('acVoiceStarting')}</span>}
          {voice.state === 'on' && (
            <>
              {voice.status === 'empty' ? (
                <span className="ac-warn">{t(service === 'microsoft' ? 'acVoiceEmptyEdge' : 'acVoiceEmpty')}</span>
              ) : (
                <span className="ac-muted">{voice.local ? t('acVoiceLocal') : t(CLOUD_TEXT[service])}</span>
              )}
              {voice.status === 'network' && <span className="ac-warn">{t('acVoiceNetwork')}</span>}
              <span className="ac-heard" ref={heardRef} />
            </>
          )}
          {voice.state === 'problem' && <span>{t(PROBLEM_TEXT[voice.problem])}</span>}
          {voice.state === 'on' && meterNote && <span className="ac-diag">{meterNote}</span>}
        </div>
      )}
      <div className="ac-stage">
        {error && <div className="ac-empty">{error}</div>}
        {!error && !data && <div className="ac-empty">{t('loading')}</div>}
        {!error && data && !rundown && (
          <div className="ac-empty">{data.unloaded ? `${t('acUnloaded')} (${t('acUnloadedAt')} ${fmtDateTime(data.unloaded)})` : t('acNone')}</div>
        )}
        {rundown && (
          <>
            <div className="ac-guide" aria-hidden="true" />
            <div
              ref={screenRef}
              className="ac-screen"
              tabIndex={0}
              onKeyDown={onKey}
              onScroll={onScroll}
              onWheel={() => handMove(400)}
              onPointerDown={pressed}
              onTouchStart={() => handMove(60_000)}
              onTouchMove={() => handMove(60_000)}
              onTouchEnd={() => (ownRef.current = performance.now() + 1800)}
              style={{ fontSize: prefs.size }}
              aria-label="Autocue"
            >
              <div className="ac-pad top" />
              <div className={classes('ac-text', prefs.mirror && 'mirror')}>
                {data!.items.map((it) =>
                  it.kind === 'separator' ? (
                    <div key={it.id} id={`ac-${it.id}`} data-ac={it.id} className="ac-sep">
                      {it.title}
                    </div>
                  ) : (
                    <Story key={it.id} item={it} reads={script.reads.get(it.id) ?? NO_READS} air={it.id === data!.onAir} next={it.id === data!.cued} />
                  ),
                )}
                <div className="ac-end">{t('acEnd')}</div>
              </div>
              <div className="ac-pad bottom" />
            </div>
            {/* story to story, on the text itself (full screen too); with the voice on, the voice's place */}
            <div className="ac-jump">
              <button
                type="button"
                onClick={() => {
                  storyStep(-1);
                  screenRef.current?.focus({ preventScroll: true });
                }}
                title={t('acPrevStory')}
                aria-label={t('acPrevStory')}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => {
                  storyStep(1);
                  screenRef.current?.focus({ preventScroll: true });
                }}
                title={t('acNextStory')}
                aria-label={t('acNextStory')}
              >
                ▼
              </button>
            </div>
          </>
        )}
      </div>
      <p className="ac-hint">
        {t('acHint')}
        {voiceMode !== 'off' && ` · ${t('acHintVoice')}`}
      </p>
      {cover && (
        <button type="button" className={classes('ac-exit', exitShown && 'shown')} onClick={() => setCover(false)} aria-label={t('acExitFull')} title={t('acExitFull')}>
          ✕
        </button>
      )}
    </div>
  );
}

/** Where an element sits in the scrolled text of the screen, in pixels from its top. */
function topIn(screen: HTMLElement, el: HTMLElement): number {
  return el.getBoundingClientRect().top - screen.getBoundingClientRect().top + screen.scrollTop;
}

/** A story: its slug, then what the presenter reads, word by word (the voice dims the words said). */
const Story = memo(function Story({ item, reads, air, next }: { item: AutocueItem; reads: ScriptRead[]; air: boolean; next: boolean }) {
  return (
    <section id={`ac-${item.id}`} data-ac={item.id} className={classes('ac-story', air && 'air', next && 'next')}>
      <div className="ac-slug">
        <span className="ac-n">{item.order || ''}</span>
        {item.displayNumber && item.displayNumber.trim() !== '-' && <span className="ac-num">{item.displayNumber}</span>}
        <span className="ac-title">{item.title}</span>
        {air && <span className="ac-tag air">{t('onAir')}</span>}
        {next && <span className="ac-tag next">{t('acNext')}</span>}
      </div>
      {reads.length ? (
        reads.map((r, k) => (
          <p key={k} className={`ac-read ${r.mode}`}>
            {r.mode === 'vo' && <span className="ac-mode">V.O</span>}
            {r.pieces.map((p, i) =>
              p.at < 0 ? (
                <Fragment key={i}>{p.text}</Fragment>
              ) : (
                <span key={i} className="ac-w">
                  {p.text}
                </span>
              ),
            )}
          </p>
        ))
      ) : (
        <p className="ac-none">— {t('acNoOn')} —</p>
      )}
    </section>
  );
});
